import React, { useState, useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, Tooltip, Legend, CategoryScale, LinearScale, 
         BarElement, PointElement, LineElement, Title } from 'chart.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import * as XLSX from 'xlsx';
import '../components/App.css'

// Registrar componentes necesarios de Chart.js
ChartJS.register(
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartDataLabels,
  Title
);

// Paleta de colores predefinida
const COLOR_PALETTE = [
  '#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f',
  '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ac'
];

const ExcelChart = () => {
  const [title, setTitle] = useState('Gráfico Editable');
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [chartType, setChartType] = useState('bar');
  const [showValues, setShowValues] = useState(true);
  const [error, setError] = useState('');
  const chartRef = useRef(null);

  // Procesamiento de archivo Excel mejorado
  const handleFileUpload = (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        if (workbook.SheetNames.length === 0) {
          throw new Error('El archivo no contiene hojas');
        }

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          throw new Error('El archivo debe contener al menos una fila de encabezado y datos');
        }

        const labels = jsonData.slice(1).map(row => row[0]);
        const headers = jsonData[0].slice(1);
        
        const datasets = headers.map((colName, index) => ({
          label: colName,
          data: jsonData.slice(1).map(row => row[index + 1]),
          backgroundColor: COLOR_PALETTE[index % COLOR_PALETTE.length],
          borderColor: COLOR_PALETTE[index % COLOR_PALETTE.length],
          borderWidth: 2,
          fill: false,
        }));

        setChartData({ labels, datasets });
        setError('');
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError(`Error al procesar el archivo: ${err.message}`);
    }
  };

  // Generación de PDF mejorada
  const downloadPDF = async () => {
    try {
      const input = chartRef.current;
      if (!input) return;

      const canvas = await html2canvas(input);
      const pdf = new jsPDF('landscape');
      const imgData = canvas.toDataURL('image/png');
      
      // Calcular dimensiones para A4 landscape (297x210mm)
      const imgWidth = 280; // mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Agregar título
      pdf.setFontSize(18);
      pdf.text(title, pdf.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
      
      // Agregar imagen del gráfico
      pdf.addImage(imgData, 'PNG', 10, 30, imgWidth, imgHeight);
      pdf.save('grafico.pdf');
    } catch (err) {
      console.error('Error al generar PDF:', err);
    }
  };

  // Opciones comunes del gráfico
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: showValues,
        color: '#333',
        anchor: 'end',
        align: 'top',
        formatter: (value) => Number(value).toLocaleString(),
        font: {
          weight: 'bold',
          size: 12
        }
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,
        text: title,
        font: {
          size: 20
        }
      }
    }
  };

  return (
    <div style={{ padding: '5px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Controles superiores */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '10px', 
        marginBottom: '5px',
        alignItems: 'center'
      }}>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          style={{ padding: '8px' }}
          aria-label="Subir archivo Excel"
        />
      <div className="controls-container">
        <div style={{ display: 'flex', gap: '5px' }}>
          <button 
            onClick={() => setChartType('bar')}
            style={{ 
              backgroundColor: chartType === 'bar' ? '#4CAF50' : '#e0e0e0',
              color: chartType === 'bar' ? 'white' : 'black',width:'90px',borderRadius:'5px'
            }}
          >
            Columnas
          </button>
          <button
            onClick={() => setChartType('line')}
            style={{ 
              backgroundColor: chartType === 'line' ? '#4CAF50' : '#e0e0e0',
              color: chartType === 'line' ? 'white' : 'black',width:'90px',borderRadius:'5px'
            }}
          >
            Líneas
          </button>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input
            type="checkbox"
            checked={showValues}
            onChange={() => setShowValues(!showValues)}
          />
          Mostrar valores
        </label>
        
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título del gráfico"
          style={{ 
            padding: '8px',
            flex: '1',
            minWidth: '200px',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />

        <button
         
          onClick={downloadPDF} 
          style={{ 
            padding: '8px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            
          }}
        >
          Descargar PDF
        </button>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

      {/* Contenedor del gráfico */}
      <div ref={chartRef} style={{ 
        position: 'relative', 
        height: '50vh',
        minHeight: '350px',
        backgroundColor: '#f8f9fb',
        borderRadius: '8px',
        //boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border contorno de una caja o grafico
        padding: '20px'
      }}>
        {chartData.datasets.length > 0 ? (
          chartType === 'bar' ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <Line data={chartData} options={chartOptions} />
          )
        ) : (
          <div style={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#666'
          }}>
            Sube un archivo Excel para generar el gráfico
          </div>
        )}
      </div>
      <style>{`
          .controls-container {
            display: flex;
            gap: 10px;
            align-items: center;
            margin: 5px 0;
          }
         
      `}
      </style>
    </div>
  );
};

export default ExcelChart;